import DateFnsUtils from '@date-io/date-fns'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import { connect } from 'react-redux'
import { StyledTimePicker } from 'components/common/Inputs/styles'
import { TimePickerPaper } from 'components/common/styles'
import ProfileHelper from 'helpers/profile'
import React, { useEffect, useState } from 'react'
import { Form, Field } from 'react-final-form'
import { injectIntl } from 'react-intl'
import T from 'prop-types'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import isEmpty from 'lodash.isempty'
import { Button, FormControl, Tooltip, Grid } from '@material-ui/core'
import PreviewInputsForm from 'routes/App/JobsAdmin/PreviewInputsForm'
import getValidator, {
  VALIDATOR_IS_EMPTY,
  VALIDATOR_IS_LESS_THEN_255_SYMBOLS,
} from 'validation'
import Input from 'components/common/Form/Input'
import { Checkbox } from 'components/common/FormInputs'
import { APP_LANGUAGES } from 'constants/index'
import Psfs from 'lib/psfs/Psfs'
import SelectList from 'components/common/SelectList'
import TitleInput from 'components/common/TitleInput/TitleInput'
import { ReactComponent as ClockIcon } from 'assets/img/ic_clock.svg'
import {
  StyledQuestionIcon,
  ParametersContainer,
  DueDateSettingsLabel,
  TimePickerWrapper,
  DueDateDaysWrapper,
} from './JobWorkflowForm.styles'
import messages from '../translate'
import DeletionReasonParameters from './DeletionReasonParameters/DeletionReasonParameters'
import { DELETION_REASON_FIELDS } from './constants'

function JobWorkflowForm({
  licensee: { usaTimeFormat },
  intl: { formatMessage },
  formData,
  siteList,
  onSubmit,
  isLoadSiteList,
  fetchSiteList,
}) {
  const initialLanguages = {}
  const [initialValues, setInitialValues] = useState({
    title: initialLanguages,
    enabled: true,
    position: 0,
    sites: [],
  })
  const { sites: initialSites } = initialValues
  const [previewInputs, setPreviewInputs] = useState([])
  const [automaticDueDateTime, setAutomaticDueDateTime] = useState(null)
  const timepickerLocale = ProfileHelper.getCurrentLocale()

  Object.values(APP_LANGUAGES).forEach(({ key }) => {
    initialLanguages[key] = ''
  })

  useEffect(() => {
    const sitePsfs = {
      psfs: new Psfs(),
    }

    if (!isLoadSiteList && !siteList.size) {
      sitePsfs.psfs
        .addFilter('active', 1)
        .addFilter('visibleInUi', 1)
        .addFields(['id', 'name'])

      fetchSiteList({ ...sitePsfs })
    }
  }, [])

  useEffect(() => {
    if (formData.preview_inputs) {
      setPreviewInputs([...formData.preview_inputs])
    }
  }, [])

  useEffect(() => {
    if (formData.automatic_due_date_time) {
      const dueDateTime = new Date()
      dueDateTime.setHours(...formData.automatic_due_date_time.split(':'))
      setAutomaticDueDateTime(dueDateTime)
    }
  }, [])

  useEffect(() => {
    if (!isEmpty(formData)) {
      setInitialValues(formData)
    }
  }, [JSON.stringify(formData)])

  return (
    <Form
      onSubmit={submittedData =>
        onSubmit(submittedData, previewInputs, automaticDueDateTime)
      }
      initialValues={initialValues}
      mutators={{
        onChangeSiteList: ([value], state, { changeValue }) => {
          const siteIds = value.map(site => site && site.index)

          changeValue(state, 'sites', () => siteIds)
        },
        handleTitleClear: (value, state, { changeValue }) => {
          changeValue(state, `title.${value[0]}`, () => {})
        },
        onChangeDeletionReason: ([value], state, { changeValue }) => {
          changeValue(state, 'needs_deletion_reason_settings', () => {})
          if (value) {
            changeValue(
              state,
              'needs_deletion_reason_settings.type',
              () => DELETION_REASON_FIELDS.TEXTAREA,
            )
          } else {
            changeValue(
              state,
              'needs_deletion_reason_settings',
              () => undefined,
            )
          }
        },
        onChangeDeletionReasonField: (_, state, { changeValue }) => {
          changeValue(
            state,
            'needs_deletion_reason_settings.table',
            () => undefined,
          )
          changeValue(
            state,
            'needs_deletion_reason_settings.column',
            () => undefined,
          )
        },
      }}
      render={({
        handleSubmit,
        submitting,
        pristine,
        form: { mutators },
        values,
      }) => (
        <form onSubmit={handleSubmit} className="form-basic">
          <div className="form-group">
            <TitleInput
              handleTitleClear={mutators.handleTitleClear}
              formState={initialValues.title}>
              {Object.values(APP_LANGUAGES).map(({ key }) => {
                const isEnglish = key === 'en'
                const validators = [VALIDATOR_IS_LESS_THEN_255_SYMBOLS]

                if (isEnglish) {
                  validators.push(VALIDATOR_IS_EMPTY)
                }

                return (
                  <Field
                    name={`title[${key}]`}
                    key={`${key}_title`}
                    type="text"
                    lang={key}
                    label={`${key} ${formatMessage(messages.title)}`}
                    component={Input}
                    required={isEnglish}
                    disabled={submitting}
                    validate={getValidator(validators)}
                  />
                )
              })}
            </TitleInput>
            <FormControl className="form-group" fullWidth required>
              <Field name="sites" type="hidden" component={() => null} />
              <SelectList
                multiple
                required
                withCheckBox
                hasAllValueOption
                outMultipleValues={{
                  all: [],
                }}
                map={{
                  index: 'id',
                  text: 'name',
                }}
                variant="outlined"
                name="sites"
                disabled={submitting}
                label={formatMessage(messages.sites)}
                list={siteList}
                value={
                  initialSites.length
                    ? initialSites
                    : siteList.toArray().map(({ id }) => id)
                }
                onChange={mutators.onChangeSiteList}
              />
            </FormControl>
            <div>
              <FormControlLabel
                label={
                  <>
                    {formatMessage(messages.reasonForDeletion)}
                    <Tooltip
                      title={formatMessage(
                        messages.needToAddDeletionReasonTooltip,
                      )}
                      placement="top">
                      <StyledQuestionIcon />
                    </Tooltip>
                  </>
                }
                disabled={submitting}
                className="form-group"
                control={
                  <Field
                    name="needs_deletion_reason"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                    parse={val => {
                      mutators.onChangeDeletionReason(val)

                      return val
                    }}
                  />
                }
              />
            </div>
            {values.needs_deletion_reason && (
              <DeletionReasonParameters
                submitting={submitting}
                values={values}
                onTextAreaClick={mutators.onChangeDeletionReasonField}
              />
            )}
            <FormControlLabel
              label={formatMessage(messages.active)}
              disabled={submitting}
              className="form-group"
              control={
                <Field
                  name="enabled"
                  component={Checkbox}
                  type="checkbox"
                  color="primary"
                />
              }
            />
            <FormControl className="form-group" fullWidth>
              <FormControlLabel
                label={formatMessage(messages.setupDueDate)}
                disabled={submitting}
                className="form-group"
                control={
                  <Field
                    name="automatic_due_date"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                  />
                }
              />
            </FormControl>
            {values.automatic_due_date && (
              <ParametersContainer>
                <Grid container>
                  <DueDateSettingsLabel>
                    {formatMessage(messages.dueDateIs)}
                  </DueDateSettingsLabel>
                  <DueDateDaysWrapper>
                    <Field
                      name="automatic_due_date_days"
                      component={Input}
                      type="number"
                      disabled={submitting}
                    />
                  </DueDateDaysWrapper>
                  <DueDateSettingsLabel>{`${formatMessage(
                    messages.days,
                  )} ${formatMessage(
                    messages.afterStartAt,
                  )}`}</DueDateSettingsLabel>
                  <TimePickerWrapper>
                    <MuiPickersUtilsProvider
                      utils={DateFnsUtils}
                      locale={timepickerLocale}>
                      <StyledTimePicker
                        PopoverProps={{
                          PaperProps: {
                            component: TimePickerPaper,
                          },
                        }}
                        ampm={usaTimeFormat}
                        disabled={submitting}
                        keyboardIcon={<ClockIcon />}
                        invalidLabel=""
                        onChange={setAutomaticDueDateTime}
                        value={automaticDueDateTime}
                      />
                    </MuiPickersUtilsProvider>
                  </TimePickerWrapper>
                </Grid>
              </ParametersContainer>
            )}
            <Field
              required
              name="position"
              type="number"
              label={formatMessage(messages.position)}
              component={Input}
              disabled={submitting}
              validate={getValidator(VALIDATOR_IS_EMPTY)}
            />
            <PreviewInputsForm
              previewInputs={previewInputs}
              setPreviewInputs={setPreviewInputs}
              submitting={submitting}
              previewPositionProperty="job_preview_position"
            />
          </div>
          <div className="form-group buttons">
            <Button
              href={null}
              type="submit"
              color="primary"
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || pristine}>
              {formatMessage(messages.saveInfo)}
            </Button>
          </div>
        </form>
      )}
    />
  )
}

JobWorkflowForm.propTypes = {
  formData: T.object.isRequired,
  onSubmit: T.func.isRequired,
  intl: T.object,
}

export default connect(state => ({
  licensee: state.getIn(['profile', 'profile', 'licensee']),
}))(injectIntl(JobWorkflowForm))
